import React, { createContext, useContext, useMemo } from "react"
import { CLOSE_FRIENDS, CREATE_ITEM, CREATE_LIST } from "constants/index"
import { useAppSelector } from "appRedux/hooks"
import { PremiumFeatureKey } from "types/premium.types"

type PremiumAccessContextType = {
  checkAccess: (feature: PremiumFeatureKey, constraint?: any) => boolean;
}

const PremiumAccessContext = createContext<PremiumAccessContextType | undefined>(undefined)

export const PremiumAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const userProfile = useAppSelector((state) => state.user.profile)
  const premiumListCountLimit = useAppSelector((state) => state.initSlice.premiumLimit?.wishlistCount)
  const premiumItemCountLimit = useAppSelector((state) => state.initSlice.premiumLimit?.itemCount)
  const activePremiumFeatures = useAppSelector((state) => state.premium.activePremiumFeatures)

  const checkCloseFriendAccess = useMemo(() => {
    return (constraint?: any) => {
      if (!userProfile) return true
      if (userProfile.isPremium) return true

      if (constraint?.privacy === CLOSE_FRIENDS) return false
      if (constraint?.wishlists?.some((list: any) => list.privacy === CLOSE_FRIENDS)) return false

      return true
    }
  }, [userProfile])

  const checkListCreationLimit = useMemo(() => {
    return () => {
      if (typeof premiumListCountLimit !== "number" || !userProfile) return true
      if (userProfile.wishlistCount < premiumListCountLimit) return true
      return userProfile.isPremium !== null ? userProfile.isPremium : true
    }
  }, [userProfile?.isPremium, userProfile?.wishlistCount, premiumListCountLimit])

  const checkItemCreationLimit = useMemo(() => {
    return (constraint?: any) => {
      if (typeof premiumItemCountLimit !== "number" || !userProfile) return true
      if (userProfile.isPremium) return true
      return !constraint && userProfile.itemsCount < premiumItemCountLimit
    }
  }, [userProfile?.isPremium, userProfile?.itemsCount, premiumItemCountLimit])

  const customPremiumCheckHandlers = useMemo(() => ({
    [CLOSE_FRIENDS]: checkCloseFriendAccess,
    [CREATE_LIST]: checkListCreationLimit,
    [CREATE_ITEM]: checkItemCreationLimit
  }), [checkItemCreationLimit, checkListCreationLimit])

  const checkAccess = (feature: PremiumFeatureKey, constraint?: any) => {
    const rules = activePremiumFeatures[feature]
    if (!rules) return true
    if (!userProfile) return true
    if (rules.premiumOnly && !userProfile.isPremium) return false
    if (!rules.premiumOnly) {
      return customPremiumCheckHandlers[feature](constraint)
    }
    return true
  }

  return (
    <PremiumAccessContext.Provider value={{ checkAccess }}>
      {children}
    </PremiumAccessContext.Provider>
  )
}

export const usePremiumAccess = () => {
  const context = useContext(PremiumAccessContext)
  if (!context) throw new Error("usePremiumAccess must be used within PremiumAccessProvider")
  return context
}
